import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import { HttpStatus } from 'config/Api.Config';
import { isEmpty, keys } from 'lodash';
import ProductType from 'models/enums/ProductType';
import { bulkUpdateCustomPricePointsApi } from 'models/productManagement/customPricePoint/bulkUpdateCustomPricePoints/Api.CustomPricePoint.BulkUpdateCustomPricePoints';
import { customPricePointChangedPricesFromExcelApi } from 'models/productManagement/customPricePoint/changedPricesFromExcel/Api.CustomPricePoint.ChangedPricesFromExcel';
import { customPricePointImportFromExcelApi } from 'models/productManagement/customPricePoint/import/Api.CustomPricePoint.Import';
import { bulkUpdateNonWarrantyPricePointsApi } from 'models/productManagement/nonWarrantyPricePoint/bulkUpdateNonWarrantyPricePoints/Api.NonWarrantyPricePoint.BulkUpdateNonWarrantyPricePoints';
import { nonWarrantyPricePointChangedPricesFromExcelApi } from 'models/productManagement/nonWarrantyPricePoint/changedPricesFromExcel/Api.NonWarrantyPricePoint.ChangedPricesFromExcel';
import { nonWarrantyPricePointImportFromExcelApi } from 'models/productManagement/nonWarrantyPricePoint/import/Api.NonWarrantyPricePoint.Import';
import { productPricingPlanBulkUpdatePricingPlanClassApi } from 'models/productManagement/productPricingPlan/bulkUpdatePricingPlanClass/Api.ProductPricingPlan.BulkUpdatePricingPlanClass';
import { productPricingPlanChangedPricesFromExcelApi } from 'models/productManagement/productPricingPlan/changedPricesFromExcel/Api.ProductPricingPlan.ChangedPricesFromExcel';
import { productPricingPlanImportFromExcelApi } from 'models/productManagement/productPricingPlan/import/Api.ProductPricingPlan.Import';
import { bulkUpdateProductTierPricePointsApi } from 'models/productManagement/productTierPricePoint/bulkUpdateProductTierPricePoints/Api.ProductTierPricePoint.BulkUpdateProductTierPricePoint';
import { productTierPricePointChangedPricesFromExcelApi } from 'models/productManagement/productTierPricePoint/changedPricesFromExcel/Api.ProductTierPricePoint.ChangedPricesFromExcel';
import { productTierPricePointImportFromExcelApi } from 'models/productManagement/productTierPricePoint/import/Api.ProductTierPricePoint.Import';
import { useMemo, useState } from 'react';

type PriceImportProps = {
  productId: number;
  onSuccessImport(): void;
  productType?: string;
  isDisabled?: boolean;
};
export type PriceAdjustmentsImportPricesProps = { isImporting: boolean; type: 'import' | 'importWithPreview' };

const PriceImport = ({ productId, productType, isDisabled, onSuccessImport }: PriceImportProps) => {
  const [importPrices, setImportPrices] = useState<PriceAdjustmentsImportPricesProps>({
    isImporting: false,
    type: 'import',
  });
  const [newDistributions, setNewDistributions] = useState<Utils.PricingDistributions>();
  const { lang } = getLocales();

  async function getChangedDistributionsByProductType(formFile: File) {
    let res: Api.Res<Utils.PricingDistributions>;

    if (productType === ProductType.warrantiClassBased) {
      res = await productPricingPlanChangedPricesFromExcelApi({
        formFile,
        productId,
      });
    } else if (productType === ProductType.warrantyTierBased) {
      res = await productTierPricePointChangedPricesFromExcelApi({
        formFile,
        productId,
      });
    } else if (productType === ProductType.custom) {
      res = await customPricePointChangedPricesFromExcelApi({
        formFile,
        productId,
      });
    } else if (productType === ProductType.nonWarranty) {
      res = await nonWarrantyPricePointChangedPricesFromExcelApi({
        formFile,
        productId,
      });
    }

    if (res.status === HttpStatus.ok) {
      setNewDistributions(res.payload);
    } else addLog({ error: res.message });
  }
  return (
    <>
      <DropdownButton
        title={lang.importPrices}
        buttonMedia={<Icon class="fas-upload" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        constrainment="constrained"
        disableWidthConstraint
        disabled={isDisabled}
        buttonBody={lang.importPrices}
        subcomponents={ThemeDropdownButtonPopup}
        dropdownOptions={
          <>
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-upload" />}
              render={lang.importPrices}
              onClick={setImportPrices}
              data={{ isImporting: true, type: 'import' }}
            />
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-upload" />}
              render={lang.importPricesWithPreview}
              onClick={setImportPrices}
              data={{ isImporting: true, type: 'importWithPreview' }}
            />
          </>
        }
      />
      {importPrices.isImporting && importPrices.type === 'import' && (
        <ModalForm<
          | Api.Core.ProductPricingPlan.Import.IRequest
          | Api.Core.ProductTierPricePoint.Import.IRequest
          | Api.Core.NonWarrantyPricePoint.Import.IRequest
          | Api.Core.CustomPricePoint.Import.IRequest
        >
          subcomponents={ThemeModalFormDefault}
          dependencyArray={[]}
          title={lang.importPrices}
          default={{
            formFile: null,
          }}
          validation={(data, errors) => {
            if (!data?.formFile) errors.formFile = lang.required;
          }}
          onCancel={() => {
            setNewDistributions(null);
            setImportPrices({ isImporting: false, type: 'import' });
          }}
          onSubmit={async (data) => {
            let res: Api.Res<Utils.PricingDistributions>;

            if (productType === ProductType.warrantiClassBased) {
              res = await productPricingPlanImportFromExcelApi({
                formFile: data.formFile,
                productId,
              });
            } else if (productType === ProductType.warrantyTierBased) {
              res = await productTierPricePointImportFromExcelApi({
                formFile: data.formFile,
                productId,
              });
            } else if (productType === ProductType.custom) {
              res = await customPricePointImportFromExcelApi({
                formFile: data.formFile,
                productId,
              });
            } else if (productType === ProductType.nonWarranty) {
              res = await nonWarrantyPricePointImportFromExcelApi({
                formFile: data.formFile,
                productId,
              });
            }

            if (res.status === HttpStatus.ok) {
              addLog({ success: lang.importSuccess });
              setImportPrices({ isImporting: false, type: 'import' });
              onSuccessImport();
            } else addLog({ error: res.message });
          }}
          render={({ form }) => {
            const formFile = useMemo(
              () =>
                form.data.formFile && {
                  original: form.data.formFile,
                  type: form.data.formFile.type as Utils.File.MimeType,
                  dataUrl: window.URL.createObjectURL(form.data.formFile),
                },
              [form.data.formFile]
            );

            return (
              <FormWrapper>
                <FileInput
                  name="file"
                  fileLimit={1}
                  value={formFile ? [formFile] : []}
                  onChange={({ file }) => {
                    const formFile = file && file[0]?.original;
                    form.update({ formFile: formFile });
                  }}
                  class={ThemeFileInputDropzone}
                  error={form.errors?.formFile?._objectError}
                  label={lang.upload}
                />
              </FormWrapper>
            );
          }}
        />
      )}
      {importPrices.isImporting && importPrices.type === 'importWithPreview' && (
        <ModalForm<
          | (Api.Core.ProductPricingPlan.ChangedPricesFromExcel.IRequest &
              Api.Core.ProductPricingPlan.BulkUpdatePricingPlanClass.IRequest)
          | (Api.Core.ProductTierPricePoint.ChangedPricesFromExcel.IRequest &
              Api.Core.ProductTierPricePoint.BulkUpdateProductTierPricePoints.IRequest)
          | (Api.Core.NonWarrantyPricePoint.ChangedPricesFromExcel.IRequest &
              Api.Core.NonWarrantyPricePoint.BulkUpdateNonWarrantyPricePoints.IRequest)
          | (Api.Core.CustomPricePoint.ChangedPricesFromExcel.IRequest &
              Api.Core.CustomPricePoint.BulkUpdateCustomPricePoints.IRequest)
        >
          subcomponents={ThemeModalFormDefault}
          dependencyArray={[newDistributions]}
          title={lang.importPricesWithPreview}
          default={{
            formFile: null,
          }}
          disabled={!newDistributions || isEmpty(newDistributions)}
          onCancel={() => setImportPrices({ isImporting: false, type: 'import' })}
          onSubmit={async () => {
            if (!isEmpty(keys(newDistributions))) {
              let bulkRes: Api.Res<any>;

              if (productType === ProductType.warrantiClassBased) {
                bulkRes = await productPricingPlanBulkUpdatePricingPlanClassApi(newDistributions);
              } else if (productType === ProductType.warrantyTierBased) {
                bulkRes = await bulkUpdateProductTierPricePointsApi(newDistributions);
              } else if (productType === ProductType.custom) {
                bulkRes = await bulkUpdateCustomPricePointsApi(newDistributions);
              } else if (productType === ProductType.nonWarranty) {
                bulkRes = await bulkUpdateNonWarrantyPricePointsApi(newDistributions);
              }

              if (bulkRes.status === HttpStatus.ok) {
                addLog({ success: lang.importSuccess });
                setNewDistributions(null);
                setImportPrices({ isImporting: false, type: 'import' });
                onSuccessImport();
              } else addLog({ error: bulkRes.message });
            } else {
              addLog({ warning: lang.noChangesWereMade });
            }
          }}
          render={({ form }) => {
            const formFile = useMemo(
              () =>
                form.data.formFile && {
                  original: form.data.formFile,
                  type: form.data.formFile.type as Utils.File.MimeType,
                  dataUrl: window.URL.createObjectURL(form.data.formFile),
                },
              [form.data.formFile]
            );

            return (
              <FormWrapper>
                <p
                  style={{
                    backgroundColor: Theme.Color.success,
                    color: 'white',
                    padding: `${Theme.Size.M} ${Theme.Size.L}`,
                    fontWeight: 'bold',
                  }}
                >
                  {keys(newDistributions)?.length} price distributions will be updated for this product!
                </p>
                &nbsp;
                <FileInput
                  name="file"
                  fileLimit={1}
                  value={formFile ? [formFile] : []}
                  onChange={({ file }) => {
                    const formFile = file && file[0]?.original;
                    form.update({ formFile: formFile });
                  }}
                  class={ThemeFileInputDropzone}
                  error={form.errors?.formFile?._objectError}
                  label={lang.upload}
                />
                <Button
                  class={ThemeButtonCircle}
                  media={<Icon class="fas-upload" />}
                  render={lang.importPrices}
                  onClick={getChangedDistributionsByProductType}
                  data={formFile?.original}
                  disabled={!formFile}
                />
              </FormWrapper>
            );
          }}
        />
      )}
    </>
  );
};

export default PriceImport;
